import axios from 'axios';
import { ActionTree } from 'vuex';
import { ContractManagementHelper } from '../helpers/contract-management.helper';
import { ILoadPayload } from '../types/load-payload';
import {
  EContractManagementAction,
  EContractManagementMutation,
  getDefaultState,
} from './contract-management.constants';
import { IContractManagementState } from './contract-management.types';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';
import { ILoadPayloadDTO } from '@/api/contract-management/dtos/load-payload-dto';
import { ContractManagementAPIHelper } from '@/api/contract-management/helpers/contract-management-api.helper';
import { ContractManagementService } from '@/api/contract-management/services/contract-management.service';
import { IContractManagementFilterRanges } from '@/contract-management/types/contract-management-filter-ranges';
import { IContractManagementKeyValues } from '@/contract-management/types/contract-management-key-values';
import { IDownloadDocument } from '@/contract-management/types/download-document';
import { IEditDocumentsPayload } from '@/contract-management/types/edit-documents-payload';
import { IFolder } from '@/folders/types/folder';
import { translate } from '@/helpers/translation.helper';
import { RootState } from '@/store/store';
import { ICreateNotificationRequest } from '@/types/CreateNotificationRequest';
import { IUploadDocument } from '@/types/UploadDocument';
import { IDocument } from '@lib-shared/types/Document';
import { IDocumentNotification } from '@lib-shared/types/DocumentNotification';
import { ITag } from '@lib-shared/types/Tag';
import { FolderService } from '@/api/folders/services/folder.service';
import { IFolderTree } from '@/folders/types/folder-tree';

export const actions: ActionTree<IContractManagementState, RootState> = {
  async [EContractManagementAction.LoadDocuments]({ state, dispatch, commit }) {
    commit(EContractManagementMutation.SetLoadedDocuments, false);
    commit(EContractManagementMutation.SetLoadingDocuments, true);
    const payloadDTO: ILoadPayloadDTO = ContractManagementAPIHelper.createLoadPayloadDTO(
      state.payload
    );
    try {
      const documents: IDocument[] = await ContractManagementService.loadDocuments(payloadDTO);
      dispatch(EContractManagementAction.LoadDocumentsSuccess, documents);
    } catch (e) {
      commit(EContractManagementMutation.SetLoadedDocuments, false);
      commit(EContractManagementMutation.SetLoadingDocuments, false);
      console.error('Could not load documents', e);
    }
  },
  [EContractManagementAction.LoadDocumentsSuccess]({ commit, dispatch }, documents: IDocument[]) {
    dispatch(EContractManagementAction.DeselectAllDocuments);
    commit(EContractManagementMutation.SetDocuments, documents);
    commit(EContractManagementMutation.SetLoadingDocuments, false);
    commit(EContractManagementMutation.SetLoadedDocuments, true);
  },

  async [EContractManagementAction.LoadKeyValues]({ state, dispatch, commit }) {
    commit(EContractManagementMutation.SetLoadedKeyValues, false);
    commit(EContractManagementMutation.SetLoadingKeyValues, true);
    const payloadDTO: ILoadPayloadDTO = ContractManagementAPIHelper.createLoadPayloadDTO(
      state.payload
    );
    try {
      const keyValues: IContractManagementKeyValues = await ContractManagementService.loadKeyValues(
        payloadDTO
      );
      dispatch(EContractManagementAction.LoadKeyValuesSuccess, keyValues);
    } catch (e) {
      commit(EContractManagementMutation.SetLoadedKeyValues, false);
      commit(EContractManagementMutation.SetLoadingKeyValues, false);
      console.error('Could not load key values', e);
    }
  },
  [EContractManagementAction.LoadKeyValuesSuccess](
    { commit },
    keyValues: IContractManagementKeyValues
  ) {
    commit(EContractManagementMutation.SetKeyValues, keyValues);
    commit(EContractManagementMutation.SetLoadingKeyValues, false);
    commit(EContractManagementMutation.SetLoadedKeyValues, true);
  },

  async [EContractManagementAction.LoadDocumentNotifications]({ dispatch }) {
    try {
      const documentNotifications: IDocumentNotification[] = await ContractManagementService.loadDocumentNotifications();
      dispatch(EContractManagementAction.LoadDocumentNotificationsSuccess, documentNotifications);
    } catch (error) {
      console.error(error);
    }
  },
  [EContractManagementAction.LoadDocumentNotificationsSuccess](
    { commit },
    documentNotifications: IDocumentNotification[]
  ) {
    commit(EContractManagementMutation.SetDocumentNotifications, documentNotifications);
  },
  async [EContractManagementAction.CreateNotifications](
    { dispatch, state },
    notifications: ICreateNotificationRequest[]
  ) {
    try {
      const createdNotifications: IDocumentNotification[] = await ContractManagementService.createNotifications(
        notifications
      );
      dispatch(EContractManagementAction.CreateNotificationsSuccess, createdNotifications);
    } catch (error) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('Failed to add reminder to {documentsCount} documents', {
          documentsCount: state.selectedDocumentIds.length,
        }),
        {
          root: true,
        }
      );
    }
  },
  [EContractManagementAction.CreateNotificationsSuccess](
    { dispatch, commit },
    notifications: IDocumentNotification[]
  ) {
    commit(EContractManagementMutation.AddDocumentNotifications, notifications);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowSuccess}`,
      translate('Reminder successfully added to {documentsCount} documents', {
        documentsCount: notifications.length,
      }),
      {
        root: true,
      }
    );
  },
  async [EContractManagementAction.DeleteNotifications]({ dispatch }, ids: string[]) {
    try {
      await ContractManagementService.deleteNotifications(ids);
      dispatch(EContractManagementAction.DeleteNotificationsSuccess, ids);
    } catch (error) {
      dispatch(EContractManagementAction.DeleteNotificationsFailed, error);
    }
  },
  [EContractManagementAction.DeleteNotificationsSuccess](
    { commit },
    documentNotificationIds: string[]
  ) {
    commit(EContractManagementMutation.DeleteDocumentNotifications, documentNotificationIds);
  },
  [EContractManagementAction.DeleteNotificationsFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-remove-document-notifications'),
      {
        root: true,
      }
    );
  },

  [EContractManagementAction.ToggleSelectDocument]({ commit, state }, documentId: string) {
    const selectedDocumentIds: string[] = state.selectedDocumentIds.includes(documentId)
      ? state.selectedDocumentIds.filter((id: string) => id !== documentId)
      : [...state.selectedDocumentIds, documentId];
    commit(EContractManagementMutation.SetSelectedDocuments, selectedDocumentIds);
  },
  [EContractManagementAction.ToggleSelectAllDocuments]({ commit, state }) {
    const selectedDocumentIds: string[] =
      state.selectedDocumentIds.length === state.documents.length
        ? []
        : state.documents.map((document: IDocument) => document.id);
    commit(EContractManagementMutation.SetSelectedDocuments, selectedDocumentIds);
  },
  [EContractManagementAction.DeselectAllDocuments]({ commit }) {
    commit(EContractManagementMutation.SetSelectedDocuments, getDefaultState().selectedDocumentIds);
  },

  async [EContractManagementAction.DownloadDocuments]({ dispatch, state }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowInfo}`,
      translate('Download for {count} documents has started', {
        count: state.selectedDocumentIds.length,
      }),
      {
        root: true,
      }
    );
    try {
      const downloadDocuments: IDownloadDocument[] = await ContractManagementService.loadDownloadUrls(
        state.selectedDocumentIds
      );
      dispatch(EContractManagementAction.DownloadToClient, downloadDocuments);
    } catch (error) {
      dispatch(EContractManagementAction.DownloadFailed, { error });
    }
  },
  async [EContractManagementAction.DownloadToClient](
    { dispatch },
    downloadDocuments: IDownloadDocument[]
  ) {
    for (const { documentName, documentLink } of downloadDocuments) {
      try {
        const { data } = await axios({
          url: documentLink,
          method: 'GET',
          responseType: 'blob',
        });

        const url: string = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${documentName}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (e) {
        dispatch(EContractManagementAction.DownloadFailed, { e, downloadDocuments });
      }
    }
  },
  [EContractManagementAction.DownloadFailed]({ dispatch, state }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('Failed to download {documentsCount} documents', {
        documentsCount: state.selectedDocumentIds.length,
      }),
      {
        root: true,
      }
    );
  },

  async [EContractManagementAction.LoadFolderTree]({ commit }) {
    try {
      const folderTree: IFolderTree = await FolderService.loadFolderTree();
      folderTree.name = translate(folderTree.name);
      commit(EContractManagementMutation.LoadFolderTree, folderTree);
    } catch (error) {
      console.error(error);
    }
  },

  async [EContractManagementAction.DeleteDocuments]({ dispatch, state }) {
    try {
      await ContractManagementService.deleteDocuments(state.selectedDocumentIds);
      dispatch(EContractManagementAction.DeleteDocumentsSuccess);
    } catch (error) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('Failed to delete {documentsCount} documents', {
          documentsCount: state.selectedDocumentIds.length,
        }),
        {
          root: true,
        }
      );
    }
  },
  [EContractManagementAction.DeleteDocumentsSuccess]({ dispatch, commit }) {
    commit(EContractManagementMutation.DeleteDocuments);
    dispatch(EContractManagementAction.LoadDocuments);
    dispatch(EContractManagementAction.LoadKeyValues);
  },

  async [EContractManagementAction.UpdateDocuments](
    { dispatch, state },
    payload: IEditDocumentsPayload
  ) {
    try {
      const { user, ...formattedPayload } = payload;
      const variables: IEditDocumentsPayload = user
        ? { ...formattedPayload, userId: user.id }
        : formattedPayload;
      await ContractManagementService.editDocuments({
        payload: variables,
        documentIds: state.selectedDocumentIds,
      });
      dispatch(EContractManagementAction.UpdateDocumentsSuccess, payload);
    } catch (error) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('Failed to save the edited documents'),
        {
          root: true,
        }
      );
    }
  },
  [EContractManagementAction.UpdateDocumentsSuccess](
    { commit, dispatch, state },
    payload: IEditDocumentsPayload
  ) {
    commit(EContractManagementMutation.EditDocuments, payload);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowSuccess}`,
      translate('{documentsCount} documents successfully edited', {
        documentsCount: state.selectedDocumentIds.length,
      }),
      {
        root: true,
      }
    );
  },

  async [EContractManagementAction.LoadFilterRanges]({ commit }) {
    try {
      const filterRanges: IContractManagementFilterRanges = await ContractManagementService.loadFilterRanges();
      commit(EContractManagementMutation.SetFilterRanges, filterRanges);
    } catch (e) {
      console.error('Could not load fitler ranges', e);
    }
  },

  async [EContractManagementAction.MoveDocuments]({ dispatch, state }, folderId: string) {
    try {
      await ContractManagementService.updateDocumentsFolder(state.selectedDocumentIds, folderId);
      dispatch(EContractManagementAction.DeselectAllDocuments);
    } catch (error) {
      const folderName: string = [state.folderTree[0], ...state.folderTree.folders].find(
        (folder: IFolder) => folder.id === folderId
      ).name;
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('Failed to move {documentsCount} documents to folder "{name}"', {
          documentsCount: state.selectedDocumentIds.length,
          name: folderName,
        }),
        {
          root: true,
        }
      );
    }
  },

  async [EContractManagementAction.UploadDocument](
    { dispatch },
    { data, folderId }: { data: IUploadDocument; folderId: string }
  ) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowInfo}`,
      translate('Started upload for document {name}', {
        name: data.name,
      }),
      {
        root: true,
      }
    );
    try {
      const uploadDocument: IDocument = await ContractManagementService.uploadDocument(data);
      if (folderId) {
        await ContractManagementService.updateDocumentsFolder([uploadDocument.id], folderId);
      }
      dispatch(EContractManagementAction.UploadDocumentSuccess, uploadDocument);
    } catch (error) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('Failed to upload document "{name}"', { name: data.name }),
        {
          root: true,
        }
      );
    }
  },
  [EContractManagementAction.UploadDocumentSuccess]({ dispatch }, document: IDocument) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowSuccess}`,
      translate('Document {name} successfully uploaded', {
        name: document.name,
      }),
      {
        root: true,
      }
    );
    dispatch(EContractManagementAction.LoadDocuments);
    dispatch(EContractManagementAction.LoadKeyValues);
  },

  async [EContractManagementAction.LoadTags]({ dispatch }) {
    try {
      const allTags: ITag[] = await ContractManagementService.loadAllTags();
      dispatch(EContractManagementAction.LoadTagsSuccess, allTags);
    } catch (error) {
      console.error(error);
    }
  },
  [EContractManagementAction.LoadTagsSuccess]({ commit }, tags: ITag[]) {
    commit(EContractManagementMutation.SetTags, tags);
  },
  async [EContractManagementAction.CreateTag]({ dispatch }, title: string) {
    try {
      const createTag = await ContractManagementService.createTag(title);
      dispatch(EContractManagementAction.CreateTagSuccess, createTag);
    } catch (error) {
      dispatch(EContractManagementAction.CreateTagFailed, { error, title });
    }
  },
  [EContractManagementAction.CreateTagSuccess]({ commit }, newTag: ITag) {
    commit(EContractManagementMutation.AddTag, newTag);
  },
  [EContractManagementAction.CreateTagFailed](
    { dispatch },
    { title }: { error: string; title: string }
  ) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('Failed to create tag "{name}"', { name: title }),
      {
        root: true,
      }
    );
  },
  async [EContractManagementAction.AddTagsToDocuments](
    { dispatch, state, getters },
    selectedTags: ITag[]
  ) {
    const { selectedDocuments }: { selectedDocuments: IDocument[] } = getters;
    const updatedDocuments: IDocument[] = ContractManagementHelper.addUniqueTagsToDocuments(
      selectedDocuments,
      selectedTags
    );
    const tagIds: string[] = selectedTags.map((tag: ITag) => tag.id);
    try {
      await ContractManagementService.addTagsToDocuments(state.selectedDocumentIds, tagIds);
      dispatch(EContractManagementAction.AddTagsToDocumentsSuccess, updatedDocuments);
    } catch (error) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('Failed to add {tagsCount} tags', { tagsCount: tagIds.length }),
        {
          root: true,
        }
      );
    }
  },
  [EContractManagementAction.AddTagsToDocumentsSuccess](
    { commit, dispatch, state },
    taggedDocuments: IDocument[]
  ) {
    const updatedDocuments: IDocument[] = ContractManagementHelper.updateDocumentsWithTaggedDocuments(
      state.documents,
      taggedDocuments
    );
    commit(EContractManagementMutation.SetDocuments, updatedDocuments);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowSuccess}`,
      translate('{documentsCount} documents successfully tagged', {
        documentsCount: taggedDocuments.length,
      }),
      {
        root: true,
      }
    );
  },
  async [EContractManagementAction.RemoveAllTags]({ dispatch, state, getters }) {
    const { selectedDocuments } = getters;
    const tagIds = selectedDocuments.flatMap((document: IDocument) =>
      document.tags.map((tag: ITag) => tag.id)
    );
    const taggedDocuments = await selectedDocuments.map((selectedDocument: IDocument) => {
      selectedDocument.tags = [];
      return selectedDocument;
    });
    try {
      await ContractManagementService.deleteDocumentTags(state.selectedDocumentIds, tagIds);
      dispatch(EContractManagementAction.RemoveAllTagsSuccess, taggedDocuments);
    } catch (error) {
      console.error(error);
    }
  },
  [EContractManagementAction.RemoveAllTagsSuccess](
    { commit, dispatch, state },
    taggedDocuments: IDocument[]
  ) {
    const updatedDocuments: IDocument[] = ContractManagementHelper.updateDocumentsWithTaggedDocuments(
      state.documents,
      taggedDocuments
    );
    commit(EContractManagementMutation.SetDocuments, updatedDocuments);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowSuccess}`,
      translate('Tags successfully removed from {documentsCount} documents', {
        documentsCount: taggedDocuments.length,
      }),
      {
        root: true,
      }
    );
  },

  [EContractManagementAction.UpdatePayload](
    { commit, dispatch, state },
    value: Partial<ILoadPayload>
  ) {
    commit(EContractManagementMutation.SetPayload, { ...state.payload, ...value });
    dispatch(EContractManagementAction.LoadDocuments);
    dispatch(EContractManagementAction.LoadKeyValues);
  },
  [EContractManagementAction.SetFilterActive]({ commit }, filterActive: boolean) {
    commit(EContractManagementMutation.SetFilterActive, filterActive);
  },
  [EContractManagementAction.SetFolderIdFromRoute]({ commit, state }, folderId: string) {
    commit(EContractManagementMutation.SetPayload, { ...state.payload, folderId });
  },
  [EContractManagementAction.OpenFolder]({ commit, dispatch, state }, folderId: string) {
    commit(EContractManagementMutation.SetPayload, {
      ...state.payload,
      folderId,
      filter: { ...state.payload.filter, filterOnAllDocuments: false },
    });
    dispatch(EContractManagementAction.LoadDocuments);
    dispatch(EContractManagementAction.LoadKeyValues);
  },
};
